import { useState } from "react";
import { Avatar, Tooltip } from "@narmi/design_system";
import { Message as MessageType, ThreadSearchParams } from "../types";
import styles from "./style.module.css";
import { ThreadFile } from "./ThreadFile";
import { getContextualTimestamp } from "@utils/time";
import { useInstitutionContext } from "@hooks/institutionContext";
import { useUserConfiguration } from "@hooks/useUserConfiguration";
import { $staffApi } from "../../../api";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import { useQueryClient } from "@tanstack/react-query";
import ConfirmationDialog from "@components/ConfirmationDialog";
import { useNavigate } from "react-router-dom";
import { useNavigationContext } from "@hooks/useNavigation";
import { serializeQueryParams } from "@utils/api";

export function MessageContainer({
  message,
  files,
}: {
  message: MessageType | null;
  files: MessageType[];
}) {
  const [isHovering, setIsHovering] = useState(false);
  const { permissions } = useUserConfiguration();

  const canDelete =
    Boolean(message?.author?.is_staff || files[0]?.author?.is_staff) &&
    permissions.includes("indigo.delete_message");

  return (
    <div
      className={`${styles.messageContainer} ${message?.internal_note ? styles.internal : message?.author?.is_staff ? styles.staffMessage : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {canDelete && <DeleteButton message={message} files={files} isActive={isHovering} />}
      <div className={styles.messageContent}>
        <MessageAvatar message={message || files[0]} />
        <MessageBody message={message} files={files} />
      </div>
    </div>
  );
}

function DeleteButton({
  message,
  files,
  isActive,
}: {
  message: MessageType | null;
  files: MessageType[];
  isActive: boolean;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { sendNotification } = useNotificationContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { query } = useNavigationContext<ThreadSearchParams>();

  const { mutate: deleteMessage } = $staffApi.useMutation("post", "/api/messages/delete_bulk/", {
    onSuccess({ thread_deleted }) {
      queryClient.invalidateQueries({
        queryKey: ["get", "/v2/admin/messages/"],
      });
      queryClient.invalidateQueries({
        queryKey: ["get", "/v2/admin/threads/{uuid}/"],
      });
      if (thread_deleted) {
        sendNotification({
          type: "success",
          text: "Message deleted successfully.",
        });
        navigate(query ? `/messages/?${serializeQueryParams(query)}` : "/messages", {
          replace: true,
        });
        return;
      }
      sendNotification({
        type: "success",
        text: "Message deleted successfully.",
      });
    },
    onError() {
      sendNotification({
        type: "negative",
        text: "Unable to delete message.",
      });
    },
  });

  return (
    <>
      <div
        className={`${styles.deleteButton} ${isActive ? styles.active : ""}`}
        role="button"
        onClick={() => setIsModalOpen(true)}
      >
        <span className="narmi-icon-trash-2 fontSize--l fontColor--secondary" />
      </div>
      <ConfirmationDialog
        isOpen={isModalOpen}
        onUserDismiss={() => setIsModalOpen(false)}
        title="Delete message"
        message={`Are you sure you want to delete this message${
          files.length > 1 ? " and all attached files" : ""
        }? This cannot be undone.`}
        confirmText="Delete"
        onConfirm={() => {
          const body = files.map((file) => ({ uuid: file.id, type: file.type }));
          if (message) {
            body.push({ uuid: message.id, type: message.type });
          }
          deleteMessage({ body });
          setIsModalOpen(false);
        }}
      />
    </>
  );
}

function MessageAvatar({ message }: { message: MessageType }) {
  const { institution } = useInstitutionContext();

  const initials =
    (message.author?.first_name ?? "").charAt(0) + (message.author?.last_name ?? "").charAt(0);

  return (
    <Tooltip
      text={
        message.author?.is_staff
          ? "Staff"
          : institution.institution_type === "bank"
            ? "Customer"
            : "Member"
      }
      side="top"
    >
      <Avatar
        initials={initials}
        label={
          [message.author?.first_name, message.author?.last_name].filter(Boolean).join(" ") ||
          "Unknown"
        }
        kind="tonal"
      />
    </Tooltip>
  );
}

function MessageBody({ message, files }: { message: MessageType | null; files: MessageType[] }) {
  return (
    <div className={styles.messageBody}>
      <AuthorData message={message || files[0]} />
      {message && (
        <div
          style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "break-word" }}
        >
          {message.body}
        </div>
      )}
      {files.map((file) => {
        const { label, url } = parseBody(file.body);
        return <ThreadFile key={file.id} label={label} url={url} />;
      })}
    </div>
  );
}

function AuthorData({ message }: { message: MessageType }) {
  return (
    <div className={styles.authorData}>
      <div className="fontWeight--semibold">
        {message.author ? `${message.author.first_name} ${message.author.last_name}` : "Unknown"}
      </div>
      <div className="fontSize--s" style={{ color: "var(--color-mediumGrey)" }}>
        {getContextualTimestamp(message.created_at)}
      </div>
    </div>
  );
}

// The ThreadFile bodies will always look like:
// [Attached File](<some url>)
function parseBody(markdown: string) {
  const match = markdown.match(/\[(.*?)\]\((.*?)\)/);
  if (match) {
    return {
      label: match[1],
      url: match[2],
    };
  }
  return {
    label: "Unable to load file",
    url: "#",
  };
}
